import {
  Box,
  Divider,
  IconButton,
  Slider,
  Stack,
  Typography,
} from "@mui/material";
import { Link } from "react-router-dom";
import { useState, useRef, Fragment, useEffect } from "react";
import { ArrowRight3, Pause, VolumeHigh, VolumeSlash } from "iconsax-react";
import { useAudioPlayerContext } from "../../../context/audioPlayerContext";

interface IAudioPlayer {
  src: string;
  name?: string;
  hasBorder?: boolean;
  sxStyle?: {};
  playing?: boolean;
  onPlay?: () => void;
}

export const truncateText = (text: string, maxLength: number) => {
  if (text.length > maxLength) {
    return text.slice(0, maxLength) + "...";
  }
  return text;
};

const AudioPlayer = ({
  src,
  name,
  hasBorder,
  sxStyle,
  playing,
  onPlay,
}: Readonly<IAudioPlayer>) => {
  const audioRef = useRef<any>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const { setActiveAudio, activeAudio } = useAudioPlayerContext();

  // Format time in mm:ss
  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes}:${secs < 10 ? "0" : ""}${secs}`;
  };

  // useImperativeHandle(ref, () => ({
  //   stopFunc: () => {
  //     console.log("called stop func");
  //     setIsPlaying(false);
  //     audioRef.current.pause();
  //   },
  // }));

  // Handle play/pause toggle
  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      setActiveAudio(audio); 
      audio.play();
      setIsPlaying(true);
    }

    onPlay && onPlay();
  };

  useEffect(() => {
    if (activeAudio !== audioRef.current && isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
    }
  }, [activeAudio]);

  useEffect(() => {
    if (playing) {
      audioRef.current.play();
    } else {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  }, [playing]);

  // Update current time
  const handleTimeUpdate = () => {
    const audio = audioRef.current;
    setCurrentTime(audio.currentTime);
  };

  // Handle progress bar change
  const handleSeek = (event: any, newValue: any) => {
    const audio = audioRef.current;
    audio.currentTime = newValue;
    setCurrentTime(newValue);
  };

  // Handle volume change
  const handleVolumeChange = (event: any, newValue: any) => {
    const audio = audioRef.current;
    audio.volume = newValue;
    setVolume(newValue);
  };

  // Mute/Unmute audio
  const toggleMute = () => {
    const audio = audioRef.current;
    audio.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  // Handle metadata load to get duration
  const handleLoadedMetadata = () => {
    const audio = audioRef.current;
    setDuration(audio.duration);
  };

  // Handle end audio
  const handleEnded = () => {
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = 0;
      setIsPlaying(false);
    }
  };

  return (
    <Box
      dir={"rtl"}
      sx={{
        display: "flex",
        flexDirection: "row-reverse",
        alignItems: "center",

        gap: { xs: "0", md: 2 },
        justifyContent: "space-between",
        px: { xs: "0", md: 2 },
        py: 1,
        width: "100%",
        borderColor: (theme) => theme.palette.others.borderColor2,
        borderWidth: hasBorder ? "1px" : "0px",
        borderStyle: "solid",
        borderRadius: "8px",
        ...sxStyle,
      }}
    >
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
      />

      <IconButton onClick={togglePlayPause}>
        {isPlaying ? <Pause size="20" /> : <ArrowRight3 size="24" />}
      </IconButton>

      <Stack direction="row-reverse" gap="12px">
        <Fragment>
          <Stack
            direction={"row"}
            gap="2px"
            display={{ xs: "none", sm: "flex" }}
          >
            <Typography
              variant="footerMediumBold"
              whiteSpace="nowrap"
              color={(theme) => theme.palette.dark[40]}
            >
              {truncateText(name ?? "", 10)}
            </Typography>
          </Stack>
          <Divider
            sx={{
              display: { xs: "none", sm: "block" },
              bgcolor: (theme) => theme.palette.gray[400],
              width: "1px",
              px: "1px",
              height: "20px",
            }}
          />
        </Fragment>

        <Stack direction={"row"} gap="2px">
          <Typography
            variant="labelRegular"
            color={(theme) => theme.palette.accent.main}
          >
            {formatTime(currentTime)}
          </Typography>
          <Typography
            variant="labelRegular"
            color={(theme) => theme.palette.dominant.main}
          >
            /
          </Typography>
          <Typography
            variant="labelRegular"
            color={(theme) => theme.palette.accent.main}
          >
            {formatTime(duration)}
          </Typography>
        </Stack>
      </Stack>

      <Slider
        value={currentTime}
        max={duration}
        onChange={handleSeek}
        sx={{
          display: { xs: "block", sm: "block" },
          width: { xs: "50px", sm: "80%" },
          "& .MuiSlider-thumb": {
            width: 11, // Larger thumb
            height: 11, // Larger thumb
            bgcolor: (theme) => theme.palette.dominant.main,
          },
          "& .MuiSlider-track": {
            height: 3, // Thicker track
            bgcolor: (theme) => theme.palette.dominant.main,
            borderColor: (theme) => theme.palette.dominant.main,
          },
          "& .MuiSlider-rail": {
            height: 2, // Thicker rail
            bgcolor: (theme) => theme.palette.dark[20],
          },
        }}
        size="medium"
      />

      <IconButton onClick={toggleMute}>
        {isMuted ? <VolumeSlash size="24" /> : <VolumeHigh size="24" />}
      </IconButton>

      {/* <Slider
        value={volume}
        min={0}
        max={1}
        step={0.01}
        onChange={handleVolumeChange}
        sx={{ width: 100 }}
      /> */}

      <IconButton component={Link} to={src} target="_blank" download>
        <img src="/images/download.svg" />
      </IconButton>
    </Box>
  );
};

export default AudioPlayer;
